/**
 * La scheda "Regole" del controllo di gestione.
 *
 * Il sistema impara tre cose e non le richiede piu': come classificare un
 * movimento bancario dalla sua descrizione, a quale ingrediente corrisponde un
 * codice articolo di un fornitore, e quali voci di fattura non sono
 * ingredienti. Sono tabelle diverse ma per chi le guarda sono la stessa cosa —
 * decisioni prese una volta che continuano a valere — e la ragione per cui
 * questa pagina esiste e' che una decisione sbagliata, se non si puo'
 * correggere, resta sbagliata per sempre e in silenzio.
 *
 * Togliere una regola sulle fatture rimette in attesa le righe che aveva
 * abbinato: se la regola era sbagliata lo erano anche loro.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Wand2, Package, Ban, Trash2, Check, X } from 'lucide-react';
import ApiService, {
  CeRegola, RegolaArticolo, RegolaArticoloIgnorato, CeCategoria
} from '../services/api';
import {
  Button, Card, CardBody, Alert, Badge, Input, Select, useConfirm, useToast
} from './ui';

const Sezione = styled.section`
  margin-bottom: var(--space-2xl, 2rem);
`;

const Titolo = styled.h3`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin: 0 0 var(--space-sm);
  font-size: var(--font-size-md);
`;

const Nota = styled.p`
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  margin: 0 0 var(--space-md);
  max-width: 48rem;
  line-height: 1.5;
`;

const Tabella = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);

  th, td {
    padding: var(--space-sm);
    text-align: left;
    border-bottom: 1px solid var(--border-color);
    vertical-align: middle;
  }
  th { color: var(--text-secondary); font-weight: var(--font-weight-medium); }
  td.numero, th.numero { text-align: right; font-variant-numeric: tabular-nums; }
  tr.spenta td { opacity: 0.5; }
`;

const Azioni = styled.div`
  display: flex;
  gap: 4px;
  justify-content: flex-end;
`;

const RegoleTab: React.FC = () => {
  const conferma = useConfirm();
  const toast = useToast();

  const [regole, setRegole] = useState<CeRegola[]>([]);
  const [categorie, setCategorie] = useState<CeCategoria[]>([]);
  const [abbinati, setAbbinati] = useState<RegolaArticolo[]>([]);
  const [ignorati, setIgnorati] = useState<RegolaArticoloIgnorato[]>([]);
  const [inModifica, setInModifica] = useState<Partial<CeRegola> | null>(null);

  const carica = useCallback(async () => {
    const [r, c, articoli] = await Promise.all([
      ApiService.getCeRegole(),
      ApiService.getCeCategorie(),
      ApiService.getRegoleArticoli()
    ]);
    setRegole(r);
    setCategorie(c);
    setAbbinati(articoli.abbinati);
    setIgnorati(articoli.ignorati);
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const salvaRegola = async () => {
    if (!inModifica?.id) return;
    if (!inModifica.pattern?.trim()) {
      toast.error('Il testo da cercare non puo\' essere vuoto');
      return;
    }
    try {
      await ApiService.aggiornaCeRegola(inModifica.id, {
        nome: inModifica.nome,
        pattern: inModifica.pattern,
        tipo_match: inModifica.tipo_match,
        categoria_id: inModifica.categoria_id,
        priorita: inModifica.priorita,
        attiva: inModifica.attiva
      });
      setInModifica(null);
      await carica();
      toast.success('Regola aggiornata. Vale dalla prossima riclassificazione.');
    } catch {
      toast.error('Non riesco a salvare la regola');
    }
  };

  const eliminaRegola = async (regola: CeRegola) => {
    const ok = await conferma({
      title: 'Eliminare la regola?',
      message: 'I movimenti gia\' classificati restano come sono: la regola smette '
        + 'di valere solo da adesso in poi.',
      variant: 'danger'
    });
    if (!ok) return;
    await ApiService.eliminaCeRegola(regola.id);
    await carica();
  };

  const eliminaArticolo = async (regola: RegolaArticolo) => {
    const ok = await conferma({
      title: `Togliere l'abbinamento di ${regola.codice_articolo}?`,
      message: `Le righe di fattura abbinate a "${regola.ingrediente_nome}" con questo `
        + 'codice tornano in attesa di essere decise.',
      variant: 'danger'
    });
    if (!ok) return;
    const esito = await ApiService.eliminaRegolaArticolo(regola.id);
    await carica();
    toast.success(`${esito.righeRimesseInAttesa} righe rimesse in attesa`);
  };

  const eliminaIgnorato = async (regola: RegolaArticoloIgnorato) => {
    const esito = await ApiService.eliminaRegolaArticoloIgnorato(regola.id);
    await carica();
    toast.success(`${esito.righeRimesseInAttesa} righe rimesse in attesa`);
  };

  return (
    <>
      <Sezione>
        <Titolo><Wand2 size={18} /> Classificazione dei movimenti</Titolo>
        <Nota>
          Si applicano alla descrizione del movimento bancario, in ordine di priorita'
          decrescente: la prima che corrisponde vince. Una regola scritta troppo larga
          attira movimenti che non c'entrano — la colonna "applicata" dice quante volte
          e' scattata, ed e' il modo piu' rapido per accorgersene.
        </Nota>

        <Card><CardBody>
          <Tabella>
            <thead>
              <tr>
                <th>Testo cercato</th>
                <th>Categoria</th>
                <th>Fornitore</th>
                <th className="numero">Priorità</th>
                <th className="numero">Applicata</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {regole.map((regola) => (
                inModifica?.id === regola.id ? (
                  <tr key={regola.id}>
                    <td>
                      <Input
                        value={inModifica.pattern || ''}
                        onChange={(e) => setInModifica({ ...inModifica, pattern: e.target.value })}
                      />
                      <Select
                        value={inModifica.tipo_match || 'contiene'}
                        onChange={(e) => setInModifica({
                          ...inModifica, tipo_match: e.target.value as CeRegola['tipo_match']
                        })}
                      >
                        <option value="contiene">contiene</option>
                        <option value="inizia_con">inizia con</option>
                        <option value="regex">espressione regolare</option>
                      </Select>
                    </td>
                    <td>
                      <Select
                        value={inModifica.categoria_id ?? ''}
                        onChange={(e) => setInModifica({
                          ...inModifica,
                          categoria_id: e.target.value ? Number(e.target.value) : null
                        })}
                      >
                        {categorie.map((c) => (
                          <option key={c.id} value={c.id}>{c.nome}</option>
                        ))}
                      </Select>
                    </td>
                    <td>{regola.fornitore_nome || '—'}</td>
                    <td className="numero">
                      <Input
                        type="number"
                        style={{ width: '5rem' }}
                        value={inModifica.priorita ?? 100}
                        onChange={(e) => setInModifica({
                          ...inModifica, priorita: Number(e.target.value)
                        })}
                      />
                    </td>
                    <td className="numero">{regola.volte_applicata}</td>
                    <td>
                      <Azioni>
                        <Button $size="sm" onClick={salvaRegola}><Check size={14} /></Button>
                        <Button $size="sm" $variant="ghost" onClick={() => setInModifica(null)}>
                          <X size={14} />
                        </Button>
                      </Azioni>
                    </td>
                  </tr>
                ) : (
                  <tr key={regola.id} className={regola.attiva ? '' : 'spenta'}>
                    <td>
                      <strong>{regola.pattern}</strong>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                        {regola.tipo_match === 'contiene' ? 'contiene' : regola.tipo_match}
                        {regola.imposta_stato === 'escluso' && ' — esclude il movimento'}
                        {!regola.attiva && ' — spenta'}
                      </div>
                    </td>
                    <td>{regola.categoria_nome}</td>
                    <td>{regola.fornitore_nome || '—'}</td>
                    <td className="numero">{regola.priorita}</td>
                    <td className="numero">{regola.volte_applicata}</td>
                    <td>
                      <Azioni>
                        <Button $size="sm" $variant="ghost" onClick={() => setInModifica(regola)}>
                          modifica
                        </Button>
                        <Button $size="sm" $variant="ghost" onClick={() => eliminaRegola(regola)}>
                          <Trash2 size={14} />
                        </Button>
                      </Azioni>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </Tabella>
        </CardBody></Card>
      </Sezione>

      <Sezione>
        <Titolo>
          <Package size={18} /> Codici articolo dei fornitori
          <Badge $variant="neutral">{abbinati.length}</Badge>
        </Titolo>
        <Nota>
          Ogni riga e' un "questo codice, di questo fornitore, e' quell'ingrediente"
          deciso una volta mentre si abbinavano le fatture. Toglierlo rimette in attesa
          le righe che aveva abbinato.
        </Nota>

        {abbinati.length === 0 ? (
          <Alert $variant="info">Nessun codice articolo ancora abbinato.</Alert>
        ) : (
          <Card><CardBody>
            <Tabella>
              <thead>
                <tr>
                  <th>Fornitore</th>
                  <th>Codice</th>
                  <th>Come lo chiama lui</th>
                  <th>È il nostro</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {abbinati.map((regola) => (
                  <tr key={regola.id}>
                    <td>{regola.fornitore_nome}</td>
                    <td><code>{regola.codice_articolo}</code></td>
                    <td>{regola.descrizione_fornitore || '—'}</td>
                    <td>{regola.ingrediente_nome}</td>
                    <td>
                      <Azioni>
                        <Button $size="sm" $variant="ghost" onClick={() => eliminaArticolo(regola)}>
                          <Trash2 size={14} />
                        </Button>
                      </Azioni>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabella>
          </CardBody></Card>
        )}
      </Sezione>

      <Sezione>
        <Titolo>
          <Ban size={18} /> Voci che non sono ingredienti
          <Badge $variant="neutral">{ignorati.length}</Badge>
        </Titolo>
        <Nota>
          Trasporto, imballi, cauzioni sui bancali: spesa vera, ma fuori dal food cost.
          Se una finisce qui per sbaglio, toglila e tornera' a chiedere di essere
          abbinata.
        </Nota>

        {ignorati.length === 0 ? (
          <Alert $variant="info">Nessuna voce scartata.</Alert>
        ) : (
          <Card><CardBody>
            <Tabella>
              <thead>
                <tr>
                  <th>Fornitore</th>
                  <th>Codice</th>
                  <th>Descrizione</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {ignorati.map((regola) => (
                  <tr key={regola.id}>
                    <td>{regola.fornitore_nome || '—'}</td>
                    <td>{regola.codice_articolo ? <code>{regola.codice_articolo}</code> : '—'}</td>
                    <td>{regola.descrizione || '—'}</td>
                    <td>
                      <Azioni>
                        <Button $size="sm" $variant="ghost" onClick={() => eliminaIgnorato(regola)}>
                          <Trash2 size={14} />
                        </Button>
                      </Azioni>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabella>
          </CardBody></Card>
        )}
      </Sezione>
    </>
  );
};

export default RegoleTab;
